import { useCallback, useState } from "react";
import { NavLink, Outlet, useOutletContext } from "react-router-dom";
import { TaxpayerSpouseToggle } from "@/features/transcripts/components/taxpayer-spouse-toggle";
import { UploadTranscriptDialog } from "@/features/transcripts/components/upload-transcript-dialog";
import type { ForWho } from "@/features/transcripts/types";
import { useActiveClient, type ClientOutletContext } from "@/layouts/client-layout";
import { cn } from "@/lib/utils";

const subNavItems = [
  { label: "Tax Owed", to: ".", end: true },
  { label: "Payments", to: "payments" },
  { label: "Income", to: "income" },
  { label: "FTA Analysis", to: "fta" },
  { label: "Bankruptcy Analysis", to: "bankruptcy" },
  { label: "Requests", to: "requests" },
  { label: "Monitoring", to: "monitor" },
];

export type TranscriptsOutletContext = ClientOutletContext & {
  forWho: ForWho;
  /** Bumped after any mutation (add/edit/delete/upload) so pages refetch. */
  dataVersion: number;
  refreshData: () => void;
};

export function useTranscriptsContext() {
  return useOutletContext<TranscriptsOutletContext>();
}

/**
 * Shell for every /client/:id/transcripts/* sub-screen: a second-level pill
 * sub-nav (Tax Owed, Payments, Income, FTA, Bankruptcy, Requests, Monitoring),
 * the Upload action, and the Taxpayer/Spouse toggle shared across them. The
 * transcript-request queue and monitoring subscriptions are the real,
 * server-backed **Requests** and **Monitoring** tabs (Tier 5.2) — the earlier
 * mock in-memory queue was removed when those went live.
 */
export function ClientTranscriptsLayout() {
  const { client, onClientUpdated } = useActiveClient();
  const [forWho, setForWho] = useState<ForWho>("T");
  const [dataVersion, setDataVersion] = useState(0);
  const refreshData = useCallback(() => setDataVersion((v) => v + 1), []);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div className="flex flex-wrap items-center gap-2">
          {subNavItems.map((item) => (
            <NavLink
              key={item.label}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "rounded-full px-4 py-2 text-xs font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "border-border bg-card text-muted-foreground hover:bg-muted border",
                )
              }
            >
              {item.label}
            </NavLink>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {client.spouse && <TaxpayerSpouseToggle value={forWho} onValueChange={setForWho} />}
          <UploadTranscriptDialog clientId={client.id} forWho={forWho} onUploaded={refreshData} />
        </div>
      </div>

      <Outlet
        context={
          { client, onClientUpdated, forWho, dataVersion, refreshData } satisfies TranscriptsOutletContext
        }
      />
    </div>
  );
}
